import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Mail,
  Loader2,
  RefreshCw,
  Check,
  LogOut,
  FileText,
  LayoutDashboard,
  Timer,
  Building,
  Tag,
  Sun,
  Moon,
  Users,
  Clock,
  Bell,
  BellOff,
  BellRing,
  Menu,
  X,
  Send,
  ChevronDown,
  ChevronUp,
  Hash
} from 'lucide-react';
import { 
  isPushSupported, 
  subscribeToPush, 
  unsubscribeFromPush, 
  isSubscribed,
  sendTestNotification,
  registerServiceWorker 
} from '@/utils/pushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sidebar Component for reuse
function Sidebar({ user, isAdmin, theme, toggleTheme, pushSupported, pushEnabled, pushLoading, handlePushToggle, handleTestNotification, handleLogout, isMobile, onClose }) {
  const location = useLocation();
  
  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link
      to={to}
      onClick={onClose}
      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
        active ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
      }`}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{children}</span>
    </Link>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div>
          <h1 className="font-heading text-lg font-bold tracking-tight text-primary">
            ETELEKOM SMS
          </h1>
          <p className="text-xs text-muted-foreground">Mesaj Yönetim Paneli</p>
        </div>
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        <NavLink to="/dashboard" icon={LayoutDashboard} active={location.pathname === '/dashboard'}>
          Dashboard
        </NavLink>
        {isAdmin && (
          <NavLink to="/admin" icon={Users} active={location.pathname === '/admin'}>
            Kullanıcı Yönetimi
          </NavLink>
        )}
        <NavLink to="/logs" icon={FileText} active={location.pathname === '/logs'}>
          İşlem Logları
        </NavLink>
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-border space-y-3">
        {/* Push Notifications */}
        {pushSupported && (
          <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              {pushEnabled ? (
                <BellRing className="h-4 w-4 text-green-500" />
              ) : (
                <BellOff className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">Bildirimler</span>
            </div>
            <div className="flex items-center gap-1">
              {pushEnabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleTestNotification}
                  className="h-8 w-8 p-0"
                  title="Test bildirimi"
                >
                  <Bell className="h-4 w-4" />
                </Button>
              )}
              <Switch
                checked={pushEnabled}
                onCheckedChange={handlePushToggle}
                disabled={pushLoading}
              />
            </div>
          </div>
        )}

        {/* Theme Toggle */}
        <div className="flex items-center justify-between p-2">
          <span className="text-sm">Tema</span>
          <Button variant="outline" size="icon" onClick={toggleTheme} className="h-8 w-8">
            {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>
        </div>

        {/* User */}
        <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{user?.username}</span>
            <Badge variant="outline" className="text-xs">
              {user?.role === 'admin' ? 'Admin' : 'User'}
            </Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="h-8 w-8 p-0">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, isAdmin, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [approvingBaslik, setApprovingBaslik] = useState(null);
  const [fetchStatus, setFetchStatus] = useState({ status: 'idle', last_fetch: null, message_count: 0 });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [approvalResult, setApprovalResult] = useState(null);
  const [expandedBaslik, setExpandedBaslik] = useState(null);

  // Group messages by baslik
  const categories = [...new Set(messages.map(m => m.baslik))].filter(Boolean);
  
  const filteredMessages = selectedCategory === 'all' 
    ? messages 
    : messages.filter(m => m.baslik === selectedCategory);

  // Group filtered messages by baslik
  const groupedMessages = filteredMessages.reduce((groups, msg) => {
    const key = msg.baslik;
    if (!groups[key]) groups[key] = [];
    groups[key].push(msg);
    return groups;
  }, {});

  // Stats
  const stats = {
    total: messages.length,
    pending: messages.filter(m => m.status === 'pending').length,
    categories: categories.length,
    totalAdet: messages.reduce((sum, m) => sum + (parseInt(String(m.adet).replace(/\./g, '').replace(/,/g, '')) || 0), 0)
  };

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/messages`, {
        headers: getAuthHeader()
      });
      setMessages(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mesajlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchFromSMPP = async () => {
    setFetching(true);
    try {
      const response = await axios.post(`${API_URL}/messages/fetch`, {}, {
        headers: getAuthHeader()
      });
      setFetchStatus(response.data);
      toast.success('SMPP\'den mesajlar çekildi');
      fetchMessages();
    } catch (error) {
      toast.error('SMPP\'den mesaj çekme başarısız');
    } finally {
      setFetching(false);
    }
  };

  const getFetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/messages/fetch-status`, {
        headers: getAuthHeader()
      });
      setFetchStatus(response.data);
    } catch (error) {
      console.error('Fetch status error:', error);
    }
  }, [getAuthHeader]);

  const approveBaslik = async (baslik) => {
    // Get first message of this baslik to send as reference
    const firstMessage = messages.find(m => m.baslik === baslik && m.status === 'pending');
    if (!firstMessage) return;
    
    const messagesInBaslik = messages.filter(m => m.baslik === baslik && m.status === 'pending');
    
    setApprovingBaslik(baslik);
    try {
      const response = await axios.post(`${API_URL}/messages/approve`, {
        message_id: firstMessage.id
      }, {
        headers: getAuthHeader()
      });
      
      // Show result dialog with details
      const data = response.data;
      setApprovalResult({
        baslik: baslik,
        approved_count: data.approved_count || messagesInBaslik.length,
        toplam_gonderilen: data.toplam_gonderilen || 0,
        batch_details: data.batch_details || [],
        result_text: data.result_text || '',
        message: data.message || `${baslik} başlığı onaylandı`
      });
      
      toast.success(`${baslik} başlığı onaylandı`);
      
      // Refresh the list
      await fetchMessages();
      await getFetchStatus();
      
    } catch (error) {
      const detail = error.response?.data?.detail || 'Onaylama başarısız';
      if (error.response?.status === 409) {
        toast.warning(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setApprovingBaslik(null);
    }
  };

  // Auto refresh effect
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchMessages();
        getFetchStatus();
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fetchMessages, getFetchStatus]);

  // Initial fetch
  useEffect(() => {
    fetchMessages();
    getFetchStatus();
    
    const checkPush = async () => {
      const supported = isPushSupported();
      setPushSupported(supported);
      if (supported) {
        await registerServiceWorker();
        const subscribed = await isSubscribed();
        setPushEnabled(subscribed);
      }
    };
    checkPush();
  }, [fetchMessages, getFetchStatus]);

  const handlePushToggle = async (enabled) => {
    setPushLoading(true);
    try {
      if (enabled) {
        await subscribeToPush(token);
        setPushEnabled(true);
        toast.success('Bildirimler aktif edildi');
      } else {
        await unsubscribeFromPush(token);
        setPushEnabled(false);
        toast.success('Bildirimler devre dışı bırakıldı');
      }
    } catch (error) {
      toast.error(error.message || 'Bildirim ayarı değiştirilemedi');
      setPushEnabled(!enabled);
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      await sendTestNotification(token);
      toast.success('Test bildirimi gönderildi');
    } catch (error) {
      toast.error('Test bildirimi gönderilemedi');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getCategoryColor = (category) => {
    const colors = [
      'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
      'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30',
      'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
      'bg-orange-500/20 text-orange-600 dark:text-orange-400 border-orange-500/30',
      'bg-pink-500/20 text-pink-600 dark:text-pink-400 border-pink-500/30',
      'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border-cyan-500/30',
    ];
    const index = categories.indexOf(category) % colors.length;
    return colors[index];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sidebarProps = {
    user,
    isAdmin,
    theme,
    toggleTheme,
    pushSupported,
    pushEnabled,
    pushLoading,
    handlePushToggle,
    handleTestNotification,
    handleLogout
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 h-screen w-64 border-r border-border bg-card/80 backdrop-blur-sm z-50">
        <Sidebar {...sidebarProps} isMobile={false} onClose={() => {}} />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 border-b border-border bg-card/95 backdrop-blur-sm z-50 flex items-center justify-between px-4">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <Sidebar {...sidebarProps} isMobile={true} onClose={() => setMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>
        
        <h1 className="font-heading text-lg font-bold text-primary">ETELEKOM SMS</h1>
        
        <Button variant="ghost" size="icon" onClick={fetchMessages} disabled={loading}>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />}
        </Button>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 p-4 lg:p-6">
        {/* Header - Desktop */}
        <div className="hidden lg:flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl font-semibold">Mesaj Onay Paneli</h2>
            <p className="text-sm text-muted-foreground">
              {user?.role === 'admin' 
                ? 'Tüm bekleyen mesajları görüntüle ve onayla'
                : `Atanan başlıklar: ${user?.assigned_basliklar?.join(', ') || 'Yok'}`
              }
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Son: {fetchStatus.last_fetch ? formatDate(fetchStatus.last_fetch) : '-'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="auto-refresh"
                checked={autoRefresh}
                onCheckedChange={setAutoRefresh}
              />
              <Label htmlFor="auto-refresh" className="text-sm">Otomatik</Label>
            </div>
            {isAdmin && (
              <Button onClick={fetchFromSMPP} disabled={fetching} variant="outline">
                {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">SMPP'den Çek</span>
              </Button>
            )}
            <Button onClick={fetchMessages} disabled={loading} variant="outline">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Yenile</span>
            </Button>
          </div>
        </div>

        {/* Mobile Sub Header */}
        <div className="lg:hidden mb-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Son: {fetchStatus.last_fetch ? formatDate(fetchStatus.last_fetch) : '-'}
            </p>
            {isAdmin && (
              <Button onClick={fetchFromSMPP} disabled={fetching} size="sm" variant="outline">
                {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-1">SMPP</span>
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-4 lg:mb-6">
          <Card className="bg-card/80">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Bekleyen Başlık</p>
                  <p className="text-xl lg:text-2xl font-bold font-mono">{stats.categories}</p>
                </div>
                <Tag className="h-6 w-6 lg:h-8 lg:w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Bekleyen Mesaj</p>
                  <p className="text-xl lg:text-2xl font-bold font-mono text-yellow-500">{stats.pending}</p>
                </div>
                <Timer className="h-6 w-6 lg:h-8 lg:w-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 col-span-2 lg:col-span-1">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Toplam Adet</p>
                  <p className="text-xl lg:text-2xl font-bold font-mono text-blue-500">{stats.totalAdet.toLocaleString('tr-TR')}</p>
                </div>
                <Hash className="h-6 w-6 lg:h-8 lg:w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Başlık Groups */}
        {Object.keys(groupedMessages).length === 0 ? (
          <Card className="bg-card/80">
            <CardContent className="p-8">
              <div className="text-center py-8">
                <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-lg">Bekleyen mesaj bulunamadı</p>
                <p className="text-muted-foreground text-sm mt-1">SMPP'den yeni mesajlar geldiğinde burada görünecek</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="baslik-groups">
            {Object.entries(groupedMessages).map(([baslik, baslikMessages]) => {
              const totalAdet = baslikMessages.reduce((sum, m) => sum + (parseInt(String(m.adet).replace(/\./g, '').replace(/,/g, '')) || 0), 0);
              const isExpanded = expandedBaslik === baslik;
              const isApproving = approvingBaslik === baslik;
              
              return (
                <Card key={baslik} className="bg-card/80 overflow-hidden" data-testid={`baslik-group-${baslik}`}>
                  {/* Başlık Header - Always visible */}
                  <div className="p-4 lg:p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getCategoryColor(baslik) + ' text-sm px-3 py-1'}>
                            {baslik}
                          </Badge>
                          <Badge variant="outline" className="font-mono">
                            {baslikMessages.length} mesaj
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Building className="h-3.5 w-3.5" />
                            {baslikMessages[0]?.firma}
                          </span>
                          <span className="flex items-center gap-1 font-mono">
                            <Hash className="h-3.5 w-3.5" />
                            {totalAdet.toLocaleString('tr-TR')} adet
                          </span>
                          <span className="flex items-center gap-1">
                            <Timer className="h-3.5 w-3.5" />
                            {baslikMessages[0]?.gecen_sure} dk
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedBaslik(isExpanded ? null : baslik)}
                          className="h-9 px-2"
                          data-testid={`expand-${baslik}`}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                        <Button
                          onClick={() => approveBaslik(baslik)}
                          disabled={isApproving}
                          className="h-9 px-4"
                          data-testid={`approve-baslik-${baslik}`}
                        >
                          {isApproving ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Onaylanıyor...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Başlığı Onayla
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Message Details */}
                  {isExpanded && (
                    <div className="border-t border-border">
                      {/* Desktop Table */}
                      <div className="hidden lg:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/30">
                              <TableHead>Mesaj</TableHead>
                              <TableHead>Firma</TableHead>
                              <TableHead>Adet</TableHead>
                              <TableHead>Tür</TableHead>
                              <TableHead>Süre</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {baslikMessages.map((message) => (
                              <TableRow key={message.id}>
                                <TableCell className="max-w-md">
                                  <p className="text-sm line-clamp-2">{message.mesaj}</p>
                                </TableCell>
                                <TableCell>{message.firma}</TableCell>
                                <TableCell className="font-mono">{message.adet}</TableCell>
                                <TableCell>{message.tur}</TableCell>
                                <TableCell className="font-mono">{message.gecen_sure} dk</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {/* Mobile Cards */}
                      <div className="lg:hidden p-3 space-y-2">
                        {baslikMessages.map((message) => (
                          <div key={message.id} className="p-3 rounded-lg bg-muted/30">
                            <p className="text-sm mb-2 line-clamp-2">{message.mesaj}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span>{message.firma}</span>
                              <span className="font-mono">{message.adet} adet</span>
                              <span>{message.gecen_sure} dk</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Approval Result Dialog */}
        <Dialog open={!!approvalResult} onOpenChange={() => setApprovalResult(null)}>
          <DialogContent className="max-w-md" data-testid="approval-result-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-500">
                <Check className="h-5 w-5" />
                Onay Başarılı
              </DialogTitle>
              <DialogDescription>
                {approvalResult?.baslik} başlığı onaylandı
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {approvalResult?.toplam_gonderilen > 0 && (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-2xl font-bold font-mono text-green-500">
                    Toplam gönderilen: {approvalResult.toplam_gonderilen.toLocaleString('tr-TR')}
                  </p>
                </div>
              )}
              {approvalResult?.batch_details?.length > 0 && (
                <ScrollArea className="max-h-48">
                  <div className="space-y-1 text-sm text-muted-foreground font-mono">
                    {approvalResult.batch_details.map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                </ScrollArea>
              )}
              {!approvalResult?.toplam_gonderilen && !approvalResult?.batch_details?.length && (
                <p className="text-sm text-muted-foreground">
                  {approvalResult?.message}
                </p>
              )}
              <div className="flex justify-end">
                <Button onClick={() => setApprovalResult(null)} data-testid="close-result-dialog">
                  Tamam
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
